import { SortDirection } from '@bp/planung-graphql-types';
import { getHolidays, getSchoolDays } from '@bp/shared-planung-utils';
import { Grid, GridColumn, GridRow, ValueField } from '@bp/ui-components';
import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useAuthClaims } from '../../../hooks/useAuthClaims';
import { useMemorizedCacheTag } from '../../../hooks/useMemorizedCacheTag';
import { useUserConfigContext } from '../../../hooks/useUserConfigContext';
import { use_TimetableBlocksQuery, useSchoolYearsQuery } from '../../../types/planung-graphql-client-defs';
import styles from './TimetableBlocksYearOverview.module.scss';

export const TimetableBlocksYearOverview = observer(() => {
  const { t } = useTranslation();
  const organizationUuid = useAuthClaims().pimAuthClaims.getOrganizationUuid();
  const schoolYear = useUserConfigContext().selectedSchoolYear;
  const timetableBlockContext = useMemorizedCacheTag('TIMETABLE_BLOCK');
  const schoolYearContext = useMemorizedCacheTag('SCHOOL_YEAR');

  const [{ data: timetableBlocksData }] = use_TimetableBlocksQuery({
    variables: {
      where: {
        timetables_SOME: { versions_SINGLE: { active: true } },
        schoolYear: { uuid: schoolYear?.uuid },
      },
      options: {
        sort: [{ start: SortDirection.Asc }],
      },
    },
    context: timetableBlockContext,
  });

  const [{ data: schoolYearsData }] = useSchoolYearsQuery({
    variables: {
      organizationUuid,
    },
    context: schoolYearContext,
  });

  const timetableBlocks = timetableBlocksData?.timetableBlocks ?? [];

  const holidays = schoolYearsData?.schoolYears.find((s) => s.uuid === schoolYear?.uuid)?.holidays ?? [];
  const holidaysSet = getHolidays(
    null,
    holidays.map((h) => ({ start: dayjs(h.start), end: dayjs(h.end) })),
  );

  const daysTotal =
    timetableBlocks.reduce((prev, curr) => {
      const dayCount = getSchoolDays(dayjs(curr.start), dayjs(curr.end), holidaysSet).size;
      return prev + dayCount;
    }, 0) ?? 1;

  const daysPercent =
    timetableBlocks.reduce((prev, curr) => {
      const dayCount = getSchoolDays(dayjs(curr.start), dayjs(curr.end), holidaysSet).size;
      return prev + (100 / daysTotal) * dayCount;
    }, 0) ?? 1;

  const factorsTotalPercent =
    timetableBlocks.reduce(
      (prev, curr) => prev + (curr.teachingLoadFactor || curr.teachingLoadFactor === 0 ? curr.teachingLoadFactor : 1),
      0,
    ) ?? 1;

  return (
    <Grid className={styles['timetable-blocks-year-overview']}>
      <GridRow>
        <GridColumn width={4}>
          <ValueField gap='none' title={t('common.timeFrame')}>
            <div
              className={styles.value}
            >{`${dayjs(schoolYear?.start).format('DD.MM.YYYY')} - ${dayjs(schoolYear?.end).format('DD.MM.YYYY')}`}</div>
          </ValueField>
        </GridColumn>

        <GridColumn width={2}>
          <ValueField gap='none' title={t('timetableBlock.title', { context: 'short', count: timetableBlocks.length })}>
            <div className={styles.value}>{timetableBlocks.length}</div>
          </ValueField>
        </GridColumn>

        <GridColumn width={2}>
          <ValueField gap='none' title={`${t('timetableBlock.schoolDays')} (${t('common.sum')})`}>
            <div className={styles.value}>{daysTotal}</div>
          </ValueField>
        </GridColumn>

        <GridColumn width={2}>
          <ValueField gap='none' title={`${t('timetableBlock.schoolDays')} (${t('common.percent')})`}>
            <div className={styles.value}>{Math.round(daysPercent * 100) / 100}</div>
            <div className={styles.percent}>%</div>
          </ValueField>
        </GridColumn>

        <GridColumn width={2}>
          <ValueField
            gap='none'
            title={t('common.factor')}
            className={Math.round(factorsTotalPercent * 10000) / 100 !== 100 ? styles.error : undefined}
          >
            <div className={styles.value}>{Math.round(factorsTotalPercent * 10000) / 100}</div>
            <div className={styles.percent}>%</div>
          </ValueField>
        </GridColumn>
      </GridRow>
    </Grid>
  );
});
